import { Form, Formik, useField } from "formik";
import * as Yup from "yup";
import { yearRange } from "../validation";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import type { Client } from "@/features/clients/types";
import {
  type Form2848Data,
  type FormSubmission,
  type GenerateFor,
  MAX_MATTERS,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCheckbox } from "./formik-controls";
import { PoaRepresentativesTable } from "./poa-representatives-table";
import { TaxMattersTable } from "./tax-matters-table";

const GENERATE_FOR_OPTIONS: { value: GenerateFor; label: string }[] = [
  { value: "taxpayer", label: "Taxpayer" },
  { value: "spouse", label: "Spouse" },
  { value: "jointly", label: "Jointly" },
];

const ACTS: { name: keyof Form2848Data["acts_authorized"]; label: string }[] = [
  { name: "access_records", label: "Access my IRS records via an Intermediate Service Provider" },
  { name: "disclose", label: "Authorize disclosure to third parties" },
  { name: "substitute", label: "Substitute or add representative(s)" },
  { name: "sign_return", label: "Sign a return" },
  { name: "specific_use", label: "Specific use not recorded on the CAF" },
];

const schema = Yup.object({
  generate_for: Yup.string().oneOf(["taxpayer", "spouse", "jointly"]).required(),
  matters: Yup.array()
    .of(
      Yup.object({
        description: Yup.string().max(200, "Too long"),
        tax_form: Yup.string().max(40, "Too long"),
        years: yearRange,
      }),
    )
    .max(MAX_MATTERS),
});

function toInitialValues(data: Record<string, unknown>): Form2848Data {
  const d = data as Partial<Form2848Data>;
  const acts = d.acts_authorized ?? ({} as Partial<Form2848Data["acts_authorized"]>);
  return {
    generate_for: d.generate_for ?? "taxpayer",
    representatives: d.representatives ?? [],
    matters:
      d.matters && d.matters.length > 0
        ? d.matters
        : [{ tax_type: "", description: "", tax_form: "", years: "", return_ownership: "" }],
    acts_authorized: {
      access_records: acts.access_records ?? false,
      disclose: acts.disclose ?? false,
      substitute: acts.substitute ?? false,
      sign_return: acts.sign_return ?? false,
      specific_use: acts.specific_use ?? false,
    },
    exceptions: d.exceptions ?? "",
  };
}

function GenerateForField() {
  const [field, , helpers] = useField<GenerateFor>("generate_for");
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as GenerateFor)}
    >
      {GENERATE_FOR_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`gf-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`gf-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

function ExceptionsField() {
  const [field] = useField("exceptions");
  return (
    <div className="grid gap-2">
      <Label htmlFor="exceptions">Specific acts not authorized (exceptions)</Label>
      <Textarea id="exceptions" rows={3} {...field} />
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Form 2848 (Power of Attorney) — prefilled from the client + its
 * practitioners, with Save (persist the data), Preview PDF (open the filled PDF
 * inline), and Save to Documents (store the PDF in the client's Forms folder).
 */
export function Form2848Editor({ client, submission, onSubmissionChange }: Props) {
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
          <Card className="gap-6 p-6">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-4">
                <span className="text-foreground font-semibold">Generate for</span>
                <GenerateForField />
              </div>
              <div className="text-muted-foreground text-sm">
                <p className="text-foreground font-medium">{client.name}</p>
                {address && <p>{address}</p>}
              </div>
            </div>
          </Card>

          <section className="flex flex-col gap-4">
            <SectionHeading>Power of Attorney Given To</SectionHeading>
            <PoaRepresentativesTable />
          </section>

          <section className="flex flex-col gap-4">
            <SectionHeading>Tax Matters</SectionHeading>
            <TaxMattersTable />
          </section>

          <Card className="gap-4 p-6">
            <SectionHeading>Acts Authorized</SectionHeading>
            <div className="grid gap-3 sm:grid-cols-2">
              {ACTS.map((act) => (
                <FormikCheckbox
                  key={act.name}
                  name={`acts_authorized.${act.name}`}
                  label={act.label}
                />
              ))}
            </div>
            <ExceptionsField />
          </Card>

          <FormEditorActions
            submission={submission}
            clientId={client.id}
            onSubmissionChange={onSubmissionChange}
          />
        </Form>
    </Formik>
  );
}
